import React, { useState } from 'react';

interface SyncOptions {
    filter_hashtag?: string;
    start_date?: string;
    end_date?: string;
    include_pattern?: string;
    exclude_pattern?: string;
    use_include_regex: boolean;
    use_exclude_regex: boolean;
}

interface SyncCalendarFormProps {
    onSync: (options: SyncOptions) => void;
}

function SyncCalendarForm({ onSync }: SyncCalendarFormProps) {
    const [filterHashtag, setFilterHashtag] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [includePattern, setIncludePattern] = useState('');
    const [excludePattern, setExcludePattern] = useState('');
    const [useIncludeRegex, setUseIncludeRegex] = useState(false);
    const [useExcludeRegex, setUseExcludeRegex] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSync({
            filter_hashtag: filterHashtag || undefined,
            start_date: startDate || undefined,
            end_date: endDate || undefined,
            include_pattern: includePattern || undefined,
            exclude_pattern: excludePattern || undefined,
            use_include_regex: useIncludeRegex,
            use_exclude_regex: useExcludeRegex
        });
    };

    const handleQuickSync = () => {
        // Quick sync for last 30 days
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        onSync({
            start_date: thirtyDaysAgo.toISOString().split('T')[0],
            end_date: new Date().toISOString().split('T')[0],
            use_include_regex: false,
            use_exclude_regex: false
        });
    };

    const handleFullSync = () => {
        // Full sync from 2 years ago to future
        const twoYearsAgo = new Date();
        twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
        
        onSync({
            start_date: twoYearsAgo.toISOString().split('T')[0],
            use_include_regex: false,
            use_exclude_regex: false
        });
    };

    return (
        <div className="mb-8 bg-gray-100 p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Sync Calendar</h2>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={handleQuickSync}
                        className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 text-sm"
                    >
                        Quick Sync (30 days)
                    </button>
                    <button
                        type="button"
                        onClick={handleFullSync}
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-sm"
                    >
                        Full Sync
                    </button>
                    <button
                        type="button"
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 text-sm"
                    >
                        {isExpanded ? 'Simple' : 'Advanced'}
                    </button>
                </div>
            </div>
            
            {isExpanded && (
            <form onSubmit={handleSubmit}>
                <div className="mb-4">
                    <label className="block mb-2">Filter Hashtag (optional)</label>
                    <input
                        type="text"
                        value={filterHashtag}
                        onChange={(e) => setFilterHashtag(e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="#work, #personal, etc."
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block mb-2">Start Date</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full p-2 border rounded"
                        />
                    </div>
                    <div>
                        <label className="block mb-2">End Date (optional)</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full p-2 border rounded"
                        />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block mb-2">Include Pattern (optional)</label>
                        <div className="space-y-2">
                            <input
                                type="text"
                                value={includePattern}
                                onChange={(e) => setIncludePattern(e.target.value)}
                                className="w-full p-2 border rounded"
                                placeholder={useIncludeRegex ? "Regular expression" : "Text to include"}
                            />
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="useIncludeRegex"
                                    checked={useIncludeRegex}
                                    onChange={(e) => setUseIncludeRegex(e.target.checked)}
                                    className="mr-2"
                                />
                                <label htmlFor="useIncludeRegex" className="text-sm">Use Regex</label>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="block mb-2">Exclude Pattern (optional)</label>
                        <div className="space-y-2">
                            <input
                                type="text"
                                value={excludePattern}
                                onChange={(e) => setExcludePattern(e.target.value)}
                                className="w-full p-2 border rounded"
                                placeholder={useExcludeRegex ? "Regular expression" : "Text to exclude"}
                            />
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="useExcludeRegex"
                                    checked={useExcludeRegex}
                                    onChange={(e) => setUseExcludeRegex(e.target.checked)}
                                    className="mr-2"
                                />
                                <label htmlFor="useExcludeRegex" className="text-sm">Use Regex</label>
                            </div>
                        </div>
                    </div>
                </div>
                <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
                    Custom Sync
                </button>
            </form>
            )}
        </div>
    );
}

export default SyncCalendarForm;